import {
  Alert,
  Button,
  Select,
  Space,
  Typography,
  notification,
} from "antd";
import { useCallback, useContext, useEffect, useState } from "react";

import { Context } from "../../context";
import { Fetch, Put } from "../../fetch";

const { Title } = Typography;

/**
 * App Config JSON — raw per-document editor for the `app_config` Mongo
 * collection (ads_config, quiz_ads_config, global, ...). The app polls the
 * merged config, so a save here goes live on the next poll — no deploy.
 * Docs can only be edited, never created; unknown ids 404 on the server.
 */
const AppConfigJson = () => {
  const { token, apiUrl } = useContext(Context);

  const [docIds, setDocIds] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [docsError, setDocsError] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [text, setText] = useState("");
  const [parseError, setParseError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDocsLoading(true);
    setDocsError(null);
    Fetch(
      `${apiUrl}/v4/app_config/admin/docs`,
      token,
      (result) => {
        setDocsLoading(false);
        if (result && result.success) {
          setDocIds(result.docs || []);
        } else {
          setDocsError("Unexpected response from server");
        }
      },
      (err) => {
        setDocsLoading(false);
        setDocsError(
          `Could not load config documents (${err.message}). ` +
            "Is the API running a build with the /app_config/admin endpoints, and are you logged in with an employee account?"
        );
      }
    );
  }, [apiUrl, token]);

  const loadDoc = useCallback(
    (docId) => {
      setSelectedDoc(docId);
      setParseError(null);
      setLoading(true);
      Fetch(
        `${apiUrl}/v4/app_config/admin/doc/${docId}`,
        token,
        (result) => {
          setLoading(false);
          if (result && result.success) {
            setText(JSON.stringify(result.data, null, 2));
          } else {
            setText("");
            notification.error({
              message: "Error loading config doc",
              description: "Check console for more details",
            });
          }
        },
        () => {
          setLoading(false);
          setText("");
          notification.error({
            message: "Error loading config doc",
            description: "Check console for more details",
          });
        }
      );
    },
    [apiUrl, token]
  );

  const handleChange = (value) => {
    setText(value);
    try {
      JSON.parse(value);
      setParseError(null);
    } catch (e) {
      setParseError(e.message);
    }
  };

  const handleSave = () => {
    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      setParseError(e.message);
      return;
    }
    if (data === null || typeof data !== "object" || Array.isArray(data)) {
      setParseError("Top level must be a JSON object");
      return;
    }
    setSaving(true);
    Put(
      `${apiUrl}/v4/app_config/admin/doc/${selectedDoc}`,
      token,
      { data },
      (result) => {
        setSaving(false);
        if (result.success) {
          notification.success({
            message: "Success",
            description: `${selectedDoc} updated — live on next app config poll`,
          });
          setText(JSON.stringify(result.data, null, 2));
        } else {
          notification.error({
            message: "Error updating config doc",
            description: "Check console for more details",
          });
        }
      },
      () => {
        setSaving(false);
        notification.error({
          message: "Error updating config doc",
          description: "Check console for more details",
        });
      }
    );
  };

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <Title level={4}>App Config JSON</Title>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Live app configuration"
        description="These documents feed the app's /app_config endpoint (ads_config, quiz_ads_config, global, ...). Saving goes live to all app users on their next config poll — no deploy or restart. Double-check JSON before saving."
      />
      {docsError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Failed to load documents"
          description={docsError}
        />
      )}
      <Space style={{ marginBottom: 16 }}>
        <span>Document</span>
        <Select
          style={{ minWidth: 260 }}
          placeholder="Select a config document"
          value={selectedDoc}
          onChange={loadDoc}
          options={docIds.map((id) => ({ value: id, label: id }))}
          loading={docsLoading}
        />
      </Space>
      {selectedDoc && (
        <>
          <textarea
            style={{
              width: "100%",
              minHeight: 420,
              fontFamily: "monospace",
              fontSize: 13,
              padding: 12,
              borderColor: parseError ? "#ff4d4f" : "#d9d9d9",
              borderRadius: 6,
            }}
            spellCheck={false}
            value={loading ? "Loading…" : text}
            onChange={(e) => handleChange(e.target.value)}
            disabled={loading}
          />
          {parseError && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 8 }}
              message="Invalid JSON"
              description={parseError}
            />
          )}
          <div style={{ marginTop: 16 }}>
            <Button
              type="primary"
              loading={saving}
              disabled={loading || Boolean(parseError)}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default AppConfigJson;
